import {
  ALL_TASKS_LINKED_ITEM_ID,
  CalendarDetailsVisibility,
  ExternalConnection,
  ID,
  SyncMode,
  Task,
  Timestamp,
} from '@griply/types';

import {
  getTaskLinkedItemIds,
  LinkedItemGoal,
} from './get-task-linked-item-ids';

export type TaskCalendarSyncTarget = {
  connectionId: ID;
  calendarId: ID;
  linkedItemId: ID;
  visibility: CalendarDetailsVisibility;
};

export type GetTaskCalendarSyncTargetsOptions = {
  task: Pick<
    Task,
    | 'id'
    | 'goalId'
    | 'lifeAreaId'
    | 'completedAt'
    | 'endStrategy'
    | 'deadlineDeadline'
  >;
  connections: ExternalConnection[];
  goalsById: Map<ID, LinkedItemGoal>;
};

// The all-tasks sync only covers open tasks dated on/after its bound (start of its creation month).
// A task with no do-date/hard-deadline has no dated event to clip, so it stays eligible.
const isWithinAllTasksSyncWindow = (
  task: GetTaskCalendarSyncTargetsOptions['task'],
  syncTasksFrom: Timestamp | null | undefined,
): boolean => {
  if (task.completedAt) {
    return false;
  }
  if (!syncTasksFrom) {
    return true;
  }
  const dates = [task.endStrategy?.deadline, task.deadlineDeadline].filter(
    (date): date is Timestamp => !!date,
  );
  if (!dates.length) {
    return true;
  }
  return dates.some((date) => date.getTime() >= syncTasksFrom.getTime());
};

export const getTaskCalendarSyncTargets = ({
  task,
  connections,
  goalsById,
}: GetTaskCalendarSyncTargetsOptions): TaskCalendarSyncTarget[] => {
  const linkedItemIds = getTaskLinkedItemIds(task, goalsById);

  return connections.flatMap((connection) => {
    const calendarSettings = connection.settings?.calendarSettings;
    if (!calendarSettings) {
      return [];
    }

    const override = connection.settings?.taskOverrides?.[task.id];

    const targetIds = [...linkedItemIds];
    const allTasksSetting = calendarSettings[ALL_TASKS_LINKED_ITEM_ID];
    if (
      allTasksSetting &&
      isWithinAllTasksSyncWindow(task, allTasksSetting.syncTasksFrom)
    ) {
      targetIds.push(ALL_TASKS_LINKED_ITEM_ID);
    }

    return targetIds.flatMap((linkedItemId) => {
      const setting = calendarSettings[linkedItemId];
      if (!setting) {
        return [];
      }

      const synced = override?.synced ?? setting.mode === SyncMode.Automatic;
      if (!synced) {
        return [];
      }

      const visibility =
        override?.detailsVisibility ??
        setting.detailsVisibility ??
        CalendarDetailsVisibility.Visible;

      return [
        {
          connectionId: connection.id,
          calendarId: setting.calendarId,
          linkedItemId,
          visibility,
        },
      ];
    });
  });
};
